import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { CUSTOM_EVENT_NAME, InsuranceEvent } from '@typesApp/AppEvent'
import { CartBundle, EligibleProductStatus, ProductBundle } from '@typesApp/Product'
import { getEligibleProductStatus, shouldCheckEligibleProducts } from '@utils/claimUtil'
import { useLazyGetCartItemsQuery } from '@redux/queries/cart'
import {
  activeClaimPerformedSelector,
  initEligibleProductCheck,
  setActiveClaimRecord,
  setEmptyEligibleProducts,
} from '@redux/slices/claimStatusSlice'
import {
  connectorCartIdSelector,
  connectorCartProductsSelector,
  connectorInitCartBundle,
  connectorSessionCustomerSelector,
  loggingOutSelector,
  sessionExpiredSelector,
  sessionReadySelector,
} from '@redux/slices/connectorSlice'
import { useGetEligibleProductsMutation, useLazyGetActiveClaimRecordQuery } from '@redux/queries/claimRecords'
import { hasEligibleProductsChanged } from '@utils/hasEligibleProductsChanged'
import { useClaimRecord } from './useClaimRecord'

type Options = {
  disableInitialCheck?: boolean
}

export const useEligibleProductCheck = (options?: Options) => {
  const dispatch = useDispatch()
  const { claimRecordId, claimRecord, fetchClaimRecord } = useClaimRecord()
  const [fetchCartItems] = useLazyGetCartItemsQuery()
  const [fetchActiveClaimRecord] = useLazyGetActiveClaimRecordQuery()
  const [checkEligibleProducts] = useGetEligibleProductsMutation()

  const isLoggingOut = useSelector(loggingOutSelector)
  const isSessionReady = useSelector(sessionReadySelector)
  const isSessionExpired = useSelector(sessionExpiredSelector)

  const cartId = useSelector(connectorCartIdSelector)
  const customer = useSelector(connectorSessionCustomerSelector)
  const cartProducts = useSelector(connectorCartProductsSelector)
  const activeCheckPerformed = useSelector(activeClaimPerformedSelector)

  const isSkip = isLoggingOut || isSessionExpired || !isSessionReady

  const eventListener = useCallback(
    (event: Event) => {
      if (!isCustomEvent(event) || isSkip) return

      const insuranceEvent = event?.detail
      if (insuranceEvent?.type && insuranceEvent.type === 'onCartBundleChange') {
        console.debug('directbilling received cartbundle-event: ', insuranceEvent)
        initEligibleProductStatus()
      }
    },
    [dispatch, isSkip, claimRecordId, claimRecord?.status, customer, cartId, cartProducts, activeCheckPerformed]
  )

  useEffect(() => {
    document.addEventListener(CUSTOM_EVENT_NAME.APP, eventListener)
    return () => {
      document.removeEventListener(CUSTOM_EVENT_NAME.APP, eventListener)
    }
  }, [eventListener])

  useEffect(() => {
    if (!isSkip && !options?.disableInitialCheck) {
      dispatch(initEligibleProductCheck())
      initEligibleProductStatus()
    }
  }, [isSkip, options?.disableInitialCheck])

  const initEligibleProductStatus = debounce(async (): Promise<ClaimRecord | void> => {
    // re-fetch to ensure we have latest eligible products in claim record
    if (claimRecordId) {
      const result = await fetchClaimRecord()

      // NOTE: claim record may not immediately reflect the status on re-fetch after cart change.
      // In addition, some brands (e.g. VDE) have insurer specific eligibility restrictions so
      // the products in the claim record may NOT be eligible (due to the insurer) but the cart
      // products may be eligible.

      // Hence, we need to use the cart items and check-insurance-eligibility API if the status
      // from the claim record is NOT_ELIGIBLE
      const status = getEligibleProductStatus(result?.eligibleProducts ?? [])
      if (status === EligibleProductStatus.NOT_ELIGIBLE) {
        fetchCartItemsAndInit()
      }
      return
    }

    // cannot fetch active claim record or already performed, proceed to initialize with cart items
    if (!customer?.id || activeCheckPerformed) {
      return fetchCartItemsAndInit()
    }

    // re-fetch if active claim record exists, otherwise proceed to initialize with cart items
    const activeClaimRecord = await fetchActiveClaimRecord({ customerNumber: customer.id, cartId }).unwrap()
    if (activeClaimRecord) {
      dispatch(setActiveClaimRecord(activeClaimRecord.id))
    } else {
      fetchCartItemsAndInit()
    }
  })

  const fetchCartItemsAndInit = async (): Promise<void> => {
    try {
      const cartBundle = await fetchCartItems().unwrap()
      initWithCartItemDetails(cartBundle)
    } catch (error) {
      console.warn(error)
    }
  }

  const initWithCartItemDetails = (cartBundle: CartBundle) => {
    const latestCartProducts = cartBundle?.products ?? []

    dispatch(connectorInitCartBundle(cartBundle))

    if (
      shouldCheckEligibleProducts(claimRecord?.status) &&
      hasEligibleProductsChanged(cartProducts, latestCartProducts)
    ) {
      checkProducts(latestCartProducts)
    }
  }

  const checkProducts = (products: ProductBundle[]) => {
    if (products.length > 0) {
      checkEligibleProducts(products)
    } else {
      dispatch(setEmptyEligibleProducts())
    }
  }
}

const isCustomEvent = (event: Event): event is CustomEvent<InsuranceEvent> => {
  return event instanceof CustomEvent
}
